import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useTranslation } from 'react-i18next';
import TextEditorVariables from '../../../../components/TextEditorVariables/TextEditorVariables';
import './document.css';
import { TinyMCE_ApiKey } from '../../../../config';
import { getDocumentTypesByGroupId } from '../../../../services/documentConfigService';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { documentDeliveryI18n } from './i18n';
import { useSnackbar } from '../../../../context/SnackbarContext';

export default function DocumentCreateEditModal({
  Modal,
  Input,
  Dropdown,
  ComboBox,
  Button,
  show,
  mode = 'create',
  initialData = null,
  onClose,
  onSave,
  serviceOptions = [],
  areaOptions = [],
  deliveryTypeOptions = [],
  writeGroupOptions = [],
  writeTypeOptions = [],
  variableGroups = [],
  uploadFileToHtml,
}) {
  const { language } = useLanguage();
  const documentsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: documentDeliveryI18n });
  const { showSnackbar } = useSnackbar();
  const [form, setForm] = useState({
    name: '',
    service: '',
    area: '',
    deliveryType: '',
    writeGroup: '',
    writeType: '',
  });
  const [content, setContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [writeTypeOptionsLocal, setWriteTypeOptionsLocal] = useState([]);
  const [loadingWriteTypes, setLoadingWriteTypes] = useState(false);

  const editorKey = `${mode}-${initialData?.id ?? 'new'}`;

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const writeTypePrefillRef = useRef(null);
  const latestWriteGroupRef = useRef('');

  useEffect(() => {
    documentDeliveryI18n.changeLanguage(documentsLanguage);
  }, [documentsLanguage]);

  useEffect(() => {
    if (!show) return;

    if (mode === 'edit' && initialData) {
      writeTypePrefillRef.current =
        initialData.documentTypeId || initialData.writeTypeId || null;
      setForm({
        name: initialData.name || '',
        service: initialData.serviceId || '',
        area: initialData.businessAreaId || '',
        deliveryType: initialData.deliveryTypeId || '',
        writeGroup:
          initialData.documentGroupId || initialData.writeGroupId || '',
        writeType: initialData.documentTypeId || initialData.writeTypeId || '',
      });
      setContent(initialData.content || '');
    } else {
      writeTypePrefillRef.current = null;
      setForm({
        name: '',
        service: '',
        area: '',
        deliveryType: '',
        writeGroup: '',
        writeType: '',
      });
      setContent('');
    }

    setAttachedFile(null);
    setWriteTypeOptionsLocal([]);
  }, [show, mode, initialData]);

  const loadWriteTypes = useCallback(
    async (groupId) => {
      if (!groupId) {
        setWriteTypeOptionsLocal([]);
        setLoadingWriteTypes(false);
        writeTypePrefillRef.current = null;
        return;
      }

      try {
        setLoadingWriteTypes(true);
        const types = await getDocumentTypesByGroupId(groupId);
        if (latestWriteGroupRef.current === groupId) {
          setWriteTypeOptionsLocal(types);

          const pendingWriteTypeId = writeTypePrefillRef.current;
          if (pendingWriteTypeId) {
            const found = types.some(
              (type) => type.value === pendingWriteTypeId
            );
            setForm((prev) => {
              const nextValue = found ? pendingWriteTypeId : '';
              return prev.writeType === nextValue
                ? prev
                : { ...prev, writeType: nextValue };
            });
            writeTypePrefillRef.current = null;
          }
        }
      } catch (error) {
        if (latestWriteGroupRef.current === groupId) {
          console.error('Error al cargar tipos de escrito:', error);
          setWriteTypeOptionsLocal([]);
        }
      } finally {
        if (latestWriteGroupRef.current === groupId) {
          setLoadingWriteTypes(false);
        }
      }
    },
    [setForm]
  );

  // Cargar tipos de escrito cuando se selecciona un grupo
  useEffect(() => {
    latestWriteGroupRef.current = form.writeGroup;
    loadWriteTypes(form.writeGroup);
  }, [form.writeGroup, loadWriteTypes]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const selectedDeliveryType = deliveryTypeOptions.find(
    (o) => o.value === form.deliveryType
  );
  const selectedWriteTypeOption =
    writeTypeOptionsLocal.find((o) => o.value === form.writeType) || null;
  const isWriteTypeDisabled = !form.writeGroup || loadingWriteTypes;

  const normalize = (str) =>
    (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const automaticByLabel =
    selectedDeliveryType &&
    ['automatico', 'automatic', 'automatico(a)', 'automático'].some((term) =>
      normalize(selectedDeliveryType.label).includes(normalize(term))
    );
  const automaticById =
    selectedDeliveryType && Number(selectedDeliveryType.value) === 2;
  const isAutomaticDelivery = automaticByLabel || automaticById;

  const baseValid =
    form.name.trim().length > 2 &&
    !!form.service &&
    !!form.area &&
    !!form.deliveryType &&
    content.trim().length > 0;

  const automaticFieldsValid = !isAutomaticDelivery
    ? true
    : !!form.writeGroup && !!form.writeType;

  const isValid = baseValid && automaticFieldsValid;

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachedFile(file);
    setUploading(true);

    try {
      let html = '';
      if (uploadFileToHtml) {
        // Let backend convert DOC/DOCX/PDF to HTML
        html = await uploadFileToHtml(file);
      } else {
        // Fallback: just read as text (for .txt/.html mock)
        html = await file.text();
      }

      setContent(html);
      if (editorRef.current) {
        editorRef.current.setContent(html || '');
      }
    } catch (error) {
      console.error('Error loading template file:', error);
      if (!uploadFileToHtml) {
        showSnackbar(t('doc_delivery_modal_error_load_file'), 'error');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleInsertVariable = (placeholder) => {
    if (!placeholder) return;
    if (editorRef.current) {
      editorRef.current.insertContent(placeholder);
      // keep state in sync if needed
      const newContent = editorRef.current.getContent();
      setContent(newContent);
    }
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onSave?.({
        id: initialData?.id,
        name: form.name.trim(),
        serviceId: form.service,
        areaId: form.area,
        deliveryTypeId: form.deliveryType,
        writeGroupId: isAutomaticDelivery ? form.writeGroup : null,
        writeTypeId: isAutomaticDelivery ? form.writeType : null,
        content,
        file: attachedFile || null,
        mode,
      });
      setSaving(false);
      onClose?.();
    } catch (error) {
      console.error('Error saving template:', error);
      setSaving(false);
    }
  };

  if (!Modal || !Input || !Dropdown || !Button) return null;
  if (!show) return null;

  return (
    <Modal
      title={
        mode === 'edit'
          ? t('doc_delivery_modal_title_edit')
          : t('doc_delivery_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        saving
          ? t('doc_delivery_modal_saving')
          : mode === 'edit'
            ? t('doc_delivery_modal_primary_edit')
            : t('doc_delivery_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || saving}
      secondaryBtnTxt={t('doc_delivery_modal_cancel')}
      secondaryBtnAction={onClose}
      width={960}
      height='80vh'
    >
      <div
        className='d-flex flex-column gap-3'
        style={{ height: '100%', maxHeight: '72vh' }}
      >
        {/* TOP: inputs row + file attach */}
        <div className='row g-2 align-items-start'>
          <div className='col-12 col-md-4'>
            <Input
              label={t('doc_delivery_modal_name_label')}
              placeholder={t('doc_delivery_modal_name_placeholder')}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </div>
          <div className='col-12 col-md-4'>
            <Dropdown
              label={t('doc_delivery_modal_service_label')}
              placeholder={t('doc_delivery_modal_service_placeholder')}
              options={serviceOptions}
              value={serviceOptions.find((o) => o.value === form.service)}
              onChange={(selected) =>
                setField('service', selected?.value ?? '')
              }
            />
          </div>
          <div className='col-12 col-md-4'>
            <Dropdown
              label={t('doc_delivery_modal_area_label')}
              placeholder={t('doc_delivery_modal_area_placeholder')}
              options={areaOptions}
              value={areaOptions.find((o) => o.value === form.area)}
              onChange={(selected) => setField('area', selected?.value ?? '')}
            />
          </div>
          <div className='col-12 col-md-4'>
            <Dropdown
              label={t('doc_delivery_modal_delivery_type_label')}
              placeholder={t('doc_delivery_modal_delivery_type_placeholder')}
              options={deliveryTypeOptions}
              value={deliveryTypeOptions.find(
                (o) => o.value === form.deliveryType
              )}
              onChange={(selected) => {
                setField('deliveryType', selected?.value ?? '');
              }}
            />
          </div>
          {isAutomaticDelivery && (
            <>
              <div className='col-12 col-md-4'>
                <Dropdown
                  label={t('doc_delivery_modal_write_group_label')}
                  placeholder={t('doc_delivery_modal_write_group_placeholder')}
                  options={writeGroupOptions}
                  value={writeGroupOptions.find(
                    (o) => o.value === form.writeGroup
                  )}
                  onChange={(selected) => {
                    setField('writeGroup', selected?.value ?? '');
                    setField('writeType', ''); // Limpiar tipo al cambiar grupo
                    writeTypePrefillRef.current = null;
                  }}
                  required={isAutomaticDelivery}
                />
              </div>
              <div className='col-12 col-md-4'>
                {ComboBox ? (
                  <ComboBox
                    label={t('doc_delivery_modal_write_type_label')}
                    placeholder={
                      loadingWriteTypes
                        ? t('doc_delivery_modal_loading')
                        : t('doc_delivery_modal_write_type_placeholder')
                    }
                    options={writeTypeOptionsLocal}
                    value={selectedWriteTypeOption}
                    onChange={(selected) =>
                      setField('writeType', selected?.value ?? '')
                    }
                    isLoading={loadingWriteTypes}
                    disabled={isWriteTypeDisabled}
                    isDisabled={isWriteTypeDisabled}
                    noResultText={t('doc_delivery_modal_no_results')}
                    required={isAutomaticDelivery}
                  />
                ) : (
                  <Dropdown
                    label={t('doc_delivery_modal_write_type_label')}
                    placeholder={
                      loadingWriteTypes
                        ? t('doc_delivery_modal_loading')
                        : t('doc_delivery_modal_write_type_placeholder')
                    }
                    options={writeTypeOptionsLocal}
                    value={selectedWriteTypeOption}
                    onChange={(selected) =>
                      setField('writeType', selected?.value ?? '')
                    }
                    disabled={isWriteTypeDisabled}
                    required={isAutomaticDelivery}
                  />
                )}
              </div>
            </>
          )}
          <div className='col-12 col-md-4'>
            <div className='d-flex flex-column gap-2 h-100 justify-content-end'>
              <div className='mini fw-semibold'>
                {t('doc_delivery_modal_base_file')}
              </div>
              <div className='d-flex align-items-center gap-2'>
                <Button
                  size='sm'
                  variant='secondary'
                  iconStart='paperclip'
                  label={
                    uploading
                      ? t('doc_delivery_modal_loading')
                      : t('doc_delivery_modal_attach_button')
                  }
                  onClick={handleFileButtonClick}
                  disabled={uploading}
                />
              </div>
              {attachedFile && !uploading && (
                <span className='mini color-gray-600'>
                  {t('doc_delivery_modal_file', { name: attachedFile.name })}
                </span>
              )}
              <input
                ref={fileInputRef}
                type='file'
                accept='.doc,.docx,.pdf,.rtf,.txt,.html'
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>
        {/* BOTTOM: editor + variables columns */}
        <div className='d-flex gap-3 flex-grow-1' style={{ minHeight: 320 }}>
          {/* LEFT: editor */}
          <div className='flex-grow-1 d-flex flex-column gap-2'>
            <div className='d-flex align-items-center justify-content-between mb-1'>
              <div className='fw-medium'>
                {t('doc_delivery_modal_content_title')}
              </div>
            </div>
            <div className='border rounded-3 flex-grow-1'>
              <Editor
                key={editorKey}
                apiKey={TinyMCE_ApiKey}
                onInit={(_evt, editor) => (editorRef.current = editor)}
                value={content}
                onEditorChange={(newContent) => setContent(newContent)}
                init={{
                  menubar: true,
                  plugins:
                    'advlist autolink lists link charmap preview anchor ' +
                    'searchreplace visualblocks code fullscreen ' +
                    'insertdatetime table code help wordcount',
                  toolbar:
                    'undo redo | formatselect | ' +
                    'bold italic underline | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'table | removeformat | help',
                  branding: false,
                  // Keep editor UI in Spanish for stability (no language-pack dependency).
                  language: 'es',
                }}
              />
            </div>
          </div>
          {/* RIGHT: variables */}
          <div
            className='d-flex flex-column'
            style={{ width: 320, flexShrink: 0 }}
          >
            <TextEditorVariables
              variableGroups={variableGroups}
              onInsert={handleInsertVariable}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
